import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
	Drawer,
	Button,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	Typography,
	IconButton,
	Divider,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SyncIcon from "@material-ui/icons/Sync";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import BusinessHoursEditor from "../BusinessHoursEditor";

// Drawer largo lateral (mesmo padrão visual do ContactDrawer/
// ReminderInstancesDrawer) no lugar do Dialog estreito de antes — a tela de
// edição de conexão acumulou muitos campos (IA, horário, automações) e o
// Dialog "sm" ficava com rolagem apertada e pouco confortável de gerenciar.
const useStyles = makeStyles(theme => ({
	drawer: {
		width: "60vw",
		minWidth: 420,
		maxWidth: 760,
		flexShrink: 0,
	},
	drawerPaper: {
		width: "60vw",
		minWidth: 420,
		maxWidth: 760,
		display: "flex",
		flexDirection: "column",
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#f8fafc",
		padding: theme.spacing(1, 2),
		minHeight: 64,
		flexShrink: 0,
	},
	headerTitle: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	body: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(2, 3),
		...theme.scrollbarStyles,
	},
	footer: {
		display: "flex",
		justifyContent: "flex-end",
		gap: 8,
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		padding: theme.spacing(1.5, 2),
		flexShrink: 0,
	},

	sectionTitle: {
		fontSize: 13,
		fontWeight: 700,
		letterSpacing: 0.4,
		textTransform: "uppercase",
		color: "#64748b",
		marginTop: 20,
		marginBottom: 8,
	},
	firstSectionTitle: {
		marginTop: 0,
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	aiBox: {
		marginTop: 8,
		padding: 16,
		borderRadius: 8,
		border: "2px solid #3b82f6",
		backgroundColor: "#f8fafc",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		color: "#2576D2",
		greetingMessage: "",
		farewellMessage: "",
		isDefault: false,
		useNps: false,
		npsMessage: "",
		priceTableUrl: "",
		priceTableLastSyncAt: "",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [syncingPrices, setSyncingPrices] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				setWhatsApp(data);

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	const handleSyncPrices = async (setFieldValue) => {
		if (!whatsAppId) {
			toast.info("Salve a conexão primeiro antes de sincronizar os preços.");
			return;
		}

		try {
			setSyncingPrices(true);
			const { data } = await api.post(`/whatsapp/${whatsAppId}/sync-prices`);
			toast.success(data?.message || "Tabela de preços sincronizada com sucesso!");

			// Recarrega os dados da conexão para atualizar a Base de Conhecimento e a data de sincronização na tela
			const { data: updatedWhatsApp } = await api.get(`/whatsapp/${whatsAppId}`);
			setWhatsApp(updatedWhatsApp);
			if (setFieldValue) {
				setFieldValue("businessInfo", updatedWhatsApp.businessInfo || "");
				setFieldValue("priceTableLastSyncAt", updatedWhatsApp.priceTableLastSyncAt || "");
			}
		} catch (err) {
			toastError(err);
		} finally {
			setSyncingPrices(false);
		}
	};

	const handleSaveWhatsApp = async values => {
		const needsBusinessHours =
			values.useAi && (values.useAutoFollowUp === true || values.afterHoursAutoNotice === true);

		if (needsBusinessHours && !values.businessHours) {
			toast.error("Preencha o Horário de Atendimento Humano — obrigatório enquanto a Recuperação de Vácuo ou o Aviso de Fora de Horário estiverem ativados.");
			return;
		}

		const whatsappData = { ...values, queueIds: selectedQueueIds };

		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				await api.post("/whatsapp", whatsappData);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
	};

	return (
		<Drawer
			className={classes.drawer}
			anchor="right"
			open={open}
			onClose={handleClose}
			classes={{ paper: classes.drawerPaper }}
		>
			<Formik
				initialValues={whatsApp}
				enableReinitialize={true}
				validationSchema={SessionSchema}
				onSubmit={(values, actions) => {
					setTimeout(() => {
						handleSaveWhatsApp(values);
						actions.setSubmitting(false);
					}, 400);
				}}
			>
				{({ values, touched, errors, isSubmitting, setFieldValue }) => (
					<Form style={{ display: "flex", flexDirection: "column", height: "100%" }}>
						<div className={classes.header}>
							<div className={classes.headerTitle}>
								<IconButton onClick={handleClose} size="small">
									<CloseIcon />
								</IconButton>
								<Typography style={{ fontWeight: "bold", fontSize: "1rem" }}>
									{whatsAppId
										? i18n.t("whatsappModal.title.edit")
										: i18n.t("whatsappModal.title.add")}
								</Typography>
							</div>
						</div>

						<div className={classes.body}>
							<Typography className={`${classes.sectionTitle} ${classes.firstSectionTitle}`}>
								Dados Básicos
							</Typography>
							<div className={classes.multFieldLine} style={{ marginBottom: 12, alignItems: "center" }}>
								<Field
									as={TextField}
									label="Nome da Conexão / Marca"
									autoFocus
									name="name"
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									style={{ flex: 2 }}
								/>
								<Field
									as={TextField}
									label="Cor da Tag"
									name="color"
									type="color"
									variant="outlined"
									margin="dense"
									style={{ flex: 1, minWidth: 100 }}
								/>
								<FormControlLabel
									control={
										<Field
											as={Switch}
											color="primary"
											name="isDefault"
											checked={values.isDefault}
										/>
									}
									label="Conexão Padrão"
								/>
							</div>

							<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
								<Field
									as={TextField}
									label="👋 Mensagem de Despedida ao Encerrar Ticket (Enviada quando o atendente clica em Resolver)"
									placeholder="Ex: Atendimento finalizado com sucesso! Muito obrigado pelo contato com a Prime Nutra!"
									multiline
									rows={2}
									fullWidth
									name="farewellMessage"
									variant="outlined"
									margin="dense"
								/>

								<div style={{
									marginTop: 8,
									marginBottom: 4,
									padding: 14,
									borderRadius: 8,
									border: "1px solid #e2e8f0",
									backgroundColor: values.useNps ? "#f0fdf4" : "#f8fafc"
								}}>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="useNps"
												checked={values.useNps}
											/>
										}
										label="⭐ Ativar Pesquisa de Satisfação NPS ao Encerrar Atendimento"
									/>
									<Typography variant="caption" display="block" style={{ color: "#64748b", marginTop: 2, marginBottom: 8 }}>
										Quando ativado, envia automaticamente a pesquisa de avaliação (1 a 5) logo após a despedida ao resolver o ticket. Apenas administradores podem suprimir o envio em conversas pontuais.
									</Typography>
									{values.useNps && (
										<Field
											as={TextField}
											label="Mensagem Personalizada da Pesquisa NPS (Deixe em branco para o modelo padrão)"
											placeholder={"Por favor, avalie nosso atendimento de 1 a 5 digitando apenas o número:\n\n5️⃣ 🤩 Muito Satisfeito\n4️⃣ 🙂 Satisfeito\n3️⃣ 😐 Neutro\n2️⃣ 😕 Insatisfeito\n1️⃣ 😡 Muito Insatisfeito"}
											multiline
											rows={4}
											fullWidth
											name="npsMessage"
											variant="outlined"
											margin="dense"
										/>
									)}
								</div>

								{!values.useAi && (
									<Field
										as={TextField}
										label="💬 Mensagem Estática de Boas-Vindas (IA Desativada)"
										placeholder="Ex: Olá, seja bem-vindo! Em breve responderemos."
										multiline
										rows={2}
										fullWidth
										name="greetingMessage"
										variant="outlined"
										margin="dense"
									/>
								)}
							</div>

							<Typography className={classes.sectionTitle}>
								Inteligência Artificial
							</Typography>
							<div className={classes.aiBox}>
								<FormControlLabel
									control={
										<Field
											as={Switch}
											color="primary"
											name="useAi"
											checked={Boolean(values.useAi)}
										/>
									}
									label={<strong style={{ fontSize: 16, color: "#1e293b" }}>🤖 Ativar Atendimento Inteligente com IA (Google Gemini)</strong>}
								/>

								{values.useAi && (
									<div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
										<Field
											as={TextField}
											label="🔑 Chave de API do Google Gemini (Global ou Específica deste número)"
											placeholder="Cole sua API Key do Gemini (ex: AIzaSy... ou deixe em branco para usar a Global)"
											fullWidth
											name="geminiApiKey"
											variant="outlined"
											margin="dense"
										/>

										<div style={{
											padding: 14,
											borderRadius: 8,
											border: "1px solid #bfdbfe",
											backgroundColor: "#eff6ff",
											display: "flex",
											flexDirection: "column",
											gap: 8,
										}}>
											<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
												<Typography style={{ fontWeight: "bold", fontSize: 13, color: "#1e3a8a", display: "flex", alignItems: "center", gap: 6 }}>
													🌐 Sincronização Dinâmica de Tabela de Preços (Sistema Online)
												</Typography>
												{values.priceTableLastSyncAt && (
													<Typography variant="caption" style={{ color: "#0369a1", fontWeight: 600, backgroundColor: "#e0f2fe", padding: "2px 8px", borderRadius: 4 }}>
														Última sincronização: {values.priceTableLastSyncAt}
													</Typography>
												)}
											</div>

											<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
												<Field
													as={TextField}
													label="Link da API de Preços (PHP / JSON / Markdown)"
													placeholder="https://drastefanieviana.com.br/cartao/api/tabela.php?unidade=trairi"
													fullWidth
													name="priceTableUrl"
													variant="outlined"
													margin="dense"
													style={{ flex: 1 }}
												/>
												<Button
													variant="contained"
													color="primary"
													onClick={() => handleSyncPrices(setFieldValue)}
													disabled={syncingPrices || !whatsAppId}
													startIcon={syncingPrices ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
													style={{ height: 40, whiteSpace: "nowrap", textTransform: "none", fontWeight: "bold" }}
												>
													{syncingPrices ? "Sincronizando..." : "Sincronizar Agora"}
												</Button>
											</div>
											<Typography variant="caption" style={{ color: "#64748b" }}>
												A sincronização atualiza a Base de Conhecimento abaixo automaticamente todo dia às 04:00 da manhã de Brasília, além de espelhar as conexões irmãs.
											</Typography>
										</div>

										<Field
											as={TextField}
											label="📚 Base de Conhecimento do Negócio (Produtos, Serviços, Endereço e FAQs)"
											placeholder="Descreva aqui os detalhes dos seus produtos, serviços, endereço, valores médios e dúvidas frequentes..."
											multiline
											rows={6}
											fullWidth
											name="businessInfo"
											variant="outlined"
											margin="dense"
											inputProps={{
												style: { fontSize: 14, lineHeight: 1.5, fontFamily: "inherit" }
											}}
										/>

										<Field
											as={TextField}
											label="🎯 Missão e Roteiro da IA (System Prompt de Jornada)"
											placeholder="Descreva as etapas da jornada do cliente para este número (Etapa 1, Etapa 2, Etapa 3...)"
											multiline
											rows={8}
											fullWidth
											name="aiPrompt"
											variant="outlined"
											margin="dense"
											inputProps={{
												style: { fontSize: 14, lineHeight: 1.6, fontFamily: "inherit" }
											}}
										/>

										<Field
											as={TextField}
											label="🛑 Palavras que transferem para Humano (separadas por vírgula)"
											placeholder="falar com atendente, humano, secretária, comprar, frete"
											fullWidth
											name="transferKeywords"
											variant="outlined"
											margin="dense"
										/>

										<Divider />

										<FormControlLabel
											control={
												<Field
													as={Switch}
													color="primary"
													name="useAutoFollowUp"
													checked={values.useAutoFollowUp === true}
												/>
											}
											label={<strong style={{ fontSize: 14, color: "#047857" }}>🔄 Ativar Recuperação Automática de Leads em Vácuo (Lembretes de 2h e 24h na aba IA)</strong>}
										/>

										<FormControlLabel
											control={
												<Field
													as={Switch}
													color="primary"
													name="afterHoursAutoNotice"
													checked={values.afterHoursAutoNotice === true}
												/>
											}
											label={<strong style={{ fontSize: 14, color: "#b45309" }}>📌 Avisar Fora de Horário em Tickets já com Atendente (aba Humano)</strong>}
										/>

										{(values.useAutoFollowUp === true || values.afterHoursAutoNotice === true) && (
											<div style={{ border: "1px solid #fde68a", backgroundColor: "#fffbeb", borderRadius: 8, padding: 12 }}>
												<Typography style={{ fontWeight: "bold", fontSize: 14, color: "#1e293b", marginBottom: 8 }}>
													⏰ Horário de Atendimento Humano (obrigatório pras automações acima)
												</Typography>
												<BusinessHoursEditor
													key={whatsAppId || "new"}
													value={values.businessHours}
													onChange={json => setFieldValue("businessHours", json)}
												/>
											</div>
										)}
									</div>
								)}
							</div>
						</div>

						<div className={classes.footer}>
							<Button
								onClick={handleClose}
								color="secondary"
								disabled={isSubmitting}
								variant="outlined"
							>
								{i18n.t("whatsappModal.buttons.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								disabled={isSubmitting}
								variant="contained"
								className={classes.btnWrapper}
							>
								{whatsAppId
									? i18n.t("whatsappModal.buttons.okEdit")
									: i18n.t("whatsappModal.buttons.okAdd")}
								{isSubmitting && (
									<CircularProgress
										size={24}
										className={classes.buttonProgress}
									/>
								)}
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</Drawer>
	);
};

export default React.memo(WhatsAppModal);
